<script setup>
// v124 — aba Privilégios da caixa (pedido Kairo 13/07).
// Dá a um usuário membro da caixa visibilidade ADICIONAL de conversas por
// ETIQUETA (soma à visão do papel): modo por ATRIBUIÇÃO (todas | somente
// atribuídas a ele) e por PARTICIPANTE (todas | somente onde participa).
// Se atribuição = "todas", o bloco de participante fica irrelevante.
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PrivilegiosAPI from 'dashboard/api/privilegios';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const getters = useStoreGetters();
const allLabels = computed(() => getters['labels/getLabels'].value);

const membros = ref([]);
const regras = ref({}); // { user_id(str) => { labels, atribuicao, participante } }
const isLoading = ref(true);
const isSaving = ref(false);

const userId = ref('');
const selectedLabels = ref({}); // titulo -> bool
const atribuicao = ref('todas');
const participante = ref('participo');

const selectedTitles = computed(() =>
  Object.keys(selectedLabels.value).filter(t => selectedLabels.value[t])
);

const membroNome = id => {
  const m = membros.value.find(x => String(x.id) === String(id));
  return m ? m.name : `usuário ${id}`;
};

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await PrivilegiosAPI.getInbox(props.inbox.id);
    membros.value = data.membros || [];
    regras.value = data.regras || {};
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    isLoading.value = false;
  }
};

// Ao escolher o usuário, carrega a regra existente dele (se houver)
const onSelectUser = () => {
  const r = regras.value[String(userId.value)];
  selectedLabels.value = {};
  if (r) {
    (r.labels || []).forEach(t => {
      selectedLabels.value[t] = true;
    });
    atribuicao.value = r.atribuicao || 'todas';
    participante.value = r.participante || 'participo';
  } else {
    atribuicao.value = 'todas';
    participante.value = 'participo';
  }
};

const save = async () => {
  if (!userId.value) {
    useAlert('Selecione o usuário.');
    return;
  }
  isSaving.value = true;
  try {
    const { data } = await PrivilegiosAPI.saveInbox(props.inbox.id, {
      userId: userId.value,
      labels: selectedTitles.value,
      atribuicao: atribuicao.value,
      participante: participante.value,
    });
    regras.value = data.regras || {};
    useAlert(
      selectedTitles.value.length
        ? `Privilégios de ${membroNome(userId.value)} salvos.`
        : `Privilégios de ${membroNome(userId.value)} removidos.`
    );
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    isSaving.value = false;
  }
};

const remover = async uid => {
  isSaving.value = true;
  try {
    const { data } = await PrivilegiosAPI.saveInbox(props.inbox.id, {
      userId: uid,
      labels: [],
      atribuicao: 'todas',
      participante: 'participo',
    });
    regras.value = data.regras || {};
    useAlert(`Privilégios de ${membroNome(uid)} removidos.`);
    if (String(userId.value) === String(uid)) onSelectUser();
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    isSaving.value = false;
  }
};

const editar = uid => {
  userId.value = String(uid);
  onSelectUser();
};

const resumoRegra = r => {
  const at =
    r.atribuicao === 'todas'
      ? 'vê TODAS (qualquer atribuição)'
      : 'somente atribuídas a ele';
  const pa =
    r.participante === 'todas' ? 'todas' : 'somente onde participa';
  return r.atribuicao === 'todas' ? at : `${at} + participante: ${pa}`;
};

onMounted(load);
</script>

<template>
  <div class="mx-6 flex flex-col gap-6 max-w-4xl">
    <div>
      <h2 class="text-heading-2 text-n-slate-12 mb-1">Privilégios</h2>
      <p class="text-n-slate-11 text-sm">
        Dê a um usuário desta caixa a visualização de conversas por etiqueta,
        além do que o papel dele já permite. Escolha o usuário, marque as
        etiquetas e defina os modos. Etiquetas desmarcadas = remove o
        privilégio.
      </p>
    </div>

    <div v-if="isLoading" class="text-sm text-n-slate-11 py-6">Carregando…</div>
    <template v-else>
      <!-- Regras já configuradas -->
      <div v-if="Object.keys(regras).length" class="rounded-lg border border-n-weak p-4">
        <p class="text-sm font-medium text-n-slate-12 mb-2">
          Privilégios ativos nesta caixa
        </p>
        <div
          v-for="(r, uid) in regras"
          :key="uid"
          class="flex flex-wrap items-center gap-2 py-1.5 border-b border-n-weak last:border-b-0"
        >
          <b class="text-sm">{{ membroNome(uid) }}</b>
          <span class="text-xs text-n-slate-11 truncate">
            {{ (r.labels || []).join(', ') }} — {{ resumoRegra(r) }}
          </span>
          <span class="flex-1" />
          <button
            type="button"
            class="text-xs font-bold text-n-blue-11 hover:underline"
            @click="editar(uid)"
          >
            Editar
          </button>
          <button
            type="button"
            class="text-xs font-bold text-n-ruby-11 hover:underline"
            :disabled="isSaving"
            @click="remover(uid)"
          >
            Remover
          </button>
        </div>
      </div>

      <!-- Usuário -->
      <div class="rounded-lg border border-n-weak p-4">
        <p class="text-sm font-medium text-n-slate-12 mb-2">Usuário da caixa</p>
        <select
          v-model="userId"
          class="w-full max-w-md text-sm rounded-lg border border-n-weak bg-transparent px-3 py-2"
          @change="onSelectUser"
        >
          <option value="" disabled>Selecione o usuário…</option>
          <option v-for="m in membros" :key="m.id" :value="String(m.id)">
            {{ m.name }} ({{ m.email }})
          </option>
        </select>
      </div>

      <!-- Etiquetas -->
      <div v-if="userId" class="rounded-lg border border-n-weak p-4">
        <p class="text-sm font-medium text-n-slate-12 mb-2">Etiquetas</p>
        <div class="grid grid-cols-2 lg:grid-cols-3 gap-1 max-h-80 overflow-y-auto">
          <label
            v-for="label in allLabels"
            :key="label.id"
            class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer py-0.5"
          >
            <input
              v-model="selectedLabels[label.title]"
              type="checkbox"
              class="accent-current"
            />
            <span
              class="inline-block w-2.5 h-2.5 rounded-full shrink-0"
              :style="{ backgroundColor: label.color }"
            />
            <span class="truncate">{{ label.title }}</span>
          </label>
        </div>
      </div>

      <!-- Modo por atribuição -->
      <div v-if="userId" class="rounded-lg border border-n-weak p-4">
        <p class="text-sm font-medium text-n-slate-12 mb-2">
          Visualização por atribuição
        </p>
        <label class="flex items-start gap-2 text-sm cursor-pointer py-1">
          <input v-model="atribuicao" type="radio" value="todas" class="mt-0.5" />
          <span>
            <b>Todas as etiquetas selecionadas</b> — vê todas as conversas com
            essas etiquetas, atribuídas a qualquer usuário (ou sem atribuição).
          </span>
        </label>
        <label class="flex items-start gap-2 text-sm cursor-pointer py-1">
          <input v-model="atribuicao" type="radio" value="minhas" class="mt-0.5" />
          <span>
            <b>Somente atribuídas a ele</b> — vê as conversas com essas
            etiquetas apenas quando estiverem atribuídas a ele.
          </span>
        </label>
      </div>

      <!-- Modo por participante -->
      <div
        v-if="userId"
        class="rounded-lg border border-n-weak p-4"
        :class="{ 'opacity-50': atribuicao === 'todas' }"
      >
        <p class="text-sm font-medium text-n-slate-12 mb-1">
          Visualização por participante da conversa
        </p>
        <p v-if="atribuicao === 'todas'" class="text-xs text-n-slate-10 mb-2">
          Irrelevante: com "todas" acima, ele já vê tudo dessas etiquetas.
        </p>
        <label class="flex items-start gap-2 text-sm cursor-pointer py-1">
          <input
            v-model="participante"
            type="radio"
            value="todas"
            class="mt-0.5"
            :disabled="atribuicao === 'todas'"
          />
          <span>
            <b>Todas</b> — vê as conversas dessas etiquetas com qualquer
            participante.
          </span>
        </label>
        <label class="flex items-start gap-2 text-sm cursor-pointer py-1">
          <input
            v-model="participante"
            type="radio"
            value="participo"
            class="mt-0.5"
            :disabled="atribuicao === 'todas'"
          />
          <span>
            <b>Somente onde ele participa</b> — vê as conversas dessas
            etiquetas quando ele é participante.
          </span>
        </label>
      </div>

      <div v-if="userId" class="flex justify-end">
        <NextButton label="Salvar" :is-loading="isSaving" @click="save" />
      </div>
    </template>
  </div>
</template>
